import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const location = useLocation();

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const contactJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Lemonade Coffee Store',
    description: 'Get in touch with Lemonade Coffee Store for event catering inquiries, questions about our lemonade and Malfi Coffee drinks, or to join our mailing list.',
    url: `${siteUrl}/contact`,
    mainEntity: {
      '@type': 'Organization',
      name: 'Lemonade Coffee Store',
      email: 'info@lemonadecoffeestore.com',
      sameAs: ['https://www.instagram.com/lemonadecoffeestore/'],
      address: {
        '@type': 'PostalAddress',
        addressRegion: 'CA',
        addressLocality: 'Orange County',
        addressCountry: 'US',
      },
    },
  };

  useEffect(() => {
    if (location.hash === '#name') {
      setTimeout(() => {
        const nameInput = document.getElementById('name');
        if (nameInput) {
          nameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          nameInput.focus();
        }
      }, 100);
    }
  }, [location.hash]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('FORM_SUBMIT_URL_PLACEHOLDER', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-[#FFF8E7]">
      <SeoHead
        title="Contact Us – Lemonade Coffee Store | Orange County Event Catering"
        description="Contact Lemonade Coffee Store for event catering, questions about our signature lemonade and Malfi Coffee drinks, or to join our mailing list. Serving Orange County, CA."
        keywords="contact lemonade coffee store, coffee catering Orange County, event catering inquiry, Malfi Coffee contact"
        canonical={`${siteUrl}/contact`}
        jsonLd={contactJsonLd}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        {/* Striped Background */}
        <div className="absolute inset-0 flex">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className={`flex-1 ${i % 2 === 0 ? 'bg-[#FFD700]' : 'bg-white'}`}
            />
          ))}
        </div>
        
        <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/5 to-transparent"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-[#2C5F2D] mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Get In Touch
          </h1>
          <p className="text-xl text-[#4A4A4A] max-w-2xl mx-auto leading-relaxed">
            Have questions or want to learn more about our unique beverages? 
            We'd love to hear from you!
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-[#2C5F2D] mb-6" style={{ fontFamily: 'Georgia, serif' }}>
                Send Us a Message
              </h2>
              
              <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#2C5F2D] mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border-2 border-[#FFD700] focus:border-[#2C5F2D] focus:outline-none text-sm transition-colors"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#2C5F2D] mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border-2 border-[#FFD700] focus:border-[#2C5F2D] focus:outline-none text-sm transition-colors"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-[#2C5F2D] mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border-2 border-[#FFD700] focus:border-[#2C5F2D] focus:outline-none text-sm transition-colors"
                    placeholder="(123) 456-7890"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-[#2C5F2D] mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 rounded-lg border-2 border-[#FFD700] focus:border-[#2C5F2D] focus:outline-none text-sm transition-colors resize-none"
                    placeholder="Tell us what's on your mind..."
                  />
                  <p className="text-xs text-[#666] mt-1">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#FFD700] text-[#2C5F2D] px-8 py-4 rounded-full text-lg font-bold hover:bg-[#FFC700] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                    Oops! Something went wrong. Please try again.
                  </div>
                )}
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <h2 className="text-3xl font-bold text-[#2C5F2D] mb-6" style={{ fontFamily: 'Georgia, serif' }}>
                  Contact Us
                </h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-full flex-shrink-0">
                      <i className="ri-mail-line text-[#2C5F2D] text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2C5F2D] mb-1">Email</h3>
                      <p className="text-[#666]">sales@malficoffee.com</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-[#2C5F2D] mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                  Follow Us
                </h3>
                <p className="text-[#666] mb-6 leading-relaxed">
                  Stay updated with our latest creations and special offers!
                </p>
                <div className="flex gap-4">
                  <a href="https://www.instagram.com/lemonadecoffeestore/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center bg-[#FFD700] text-[#2C5F2D] rounded-full hover:bg-[#FFC700] transition-colors cursor-pointer">
                    <i className="ri-instagram-line text-xl"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mailing List Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-[#FFD700] rounded-full mx-auto mb-6">
            <i className="ri-mail-send-line text-[#2C5F2D] text-3xl"></i>
          </div>
          <h2 className="text-4xl font-bold text-[#2C5F2D] mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Join Our Mailing List
          </h2>
          <p className="text-[#666] text-lg leading-relaxed mb-10 max-w-xl mx-auto">
            Sign up to receive exclusive <strong>monthly subscriptions</strong>, special <strong>discounts</strong>, and be the first to know about <strong>upcoming events</strong> — straight to your inbox!
          </p>

          <div className="bg-[#FFF8E7] rounded-3xl p-10 shadow-lg text-left">
            <MailingListForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function MailingListForm() {
  const [mailingData, setMailingData] = useState({ name: '', email: '' });
  const [mailingStatus, setMailingStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isMailingSubmitting, setIsMailingSubmitting] = useState(false);

  const handleMailingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMailingData({ ...mailingData, [e.target.name]: e.target.value });
  };

  const handleMailingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsMailingSubmitting(true);
    setMailingStatus('idle');
    try {
      const body = new URLSearchParams();
      Object.entries(mailingData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d6ic06tpk1ctp7d9bek0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setMailingStatus('success');
        setMailingData({ name: '', email: '' });
      } else {
        setMailingStatus('error');
      }
    } catch {
      setMailingStatus('error');
    } finally {
      setIsMailingSubmitting(false);
    }
  };

  return (
    <form id="mailing-list-form" onSubmit={handleMailingSubmit} data-readdy-form className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label htmlFor="mailing-name" className="block text-sm font-semibold text-[#2C5F2D] mb-2">
            First Name
          </label>
          <input
            type="text"
            id="mailing-name"
            name="name"
            value={mailingData.name}
            onChange={handleMailingChange}
            required
            className="w-full px-4 py-3 rounded-lg border-2 border-[#FFD700] focus:border-[#2C5F2D] focus:outline-none text-sm transition-colors bg-white"
            placeholder="Your first name"
          />
        </div>
        <div>
          <label htmlFor="mailing-email" className="block text-sm font-semibold text-[#2C5F2D] mb-2">
            Email Address
          </label>
          <input
            type="email"
            id="mailing-email"
            name="email"
            value={mailingData.email}
            onChange={handleMailingChange}
            required
            className="w-full px-4 py-3 rounded-lg border-2 border-[#FFD700] focus:border-[#2C5F2D] focus:outline-none text-sm transition-colors bg-white"
            placeholder="your.email@example.com"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 pt-1">
        {['Monthly Deals', 'New Menu Items', 'Upcoming Events', 'Exclusive Discounts'].map((tag) => (
          <span key={tag} className="bg-[#FFD700]/40 text-[#2C5F2D] text-xs font-semibold px-3 py-1 rounded-full">
            <i className="ri-check-line mr-1"></i>{tag}
          </span>
        ))}
      </div>

      <button
        type="submit"
        disabled={isMailingSubmitting}
        className="w-full bg-[#FFD700] text-[#2C5F2D] px-8 py-4 rounded-full text-lg font-bold hover:bg-[#FFC700] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isMailingSubmitting ? 'Subscribing...' : 'Subscribe Now 🍋'}
      </button>

      {mailingStatus === 'success' && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg text-center">
          🎉 You're in! Welcome to the family — check your inbox for a confirmation.
        </div>
      )}
      {mailingStatus === 'error' && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-center">
          Oops! Something went wrong. Please try again.
        </div>
      )}

      <p className="text-xs text-[#999] text-center pt-1">
        We respect your privacy. Unsubscribe at any time. No spam, ever.
      </p>
    </form>
  );
}